import { generateKeyBetween, generateNKeysBetween } from 'fractional-indexing';
import type { WbsItemDoc } from '@mct/shared';
import { sortSiblings } from './wbsTree';

export type DropPosition = 'before' | 'after' | 'inside';

export interface DropClassifyInput {
  /** cursor Y offset within the target row (0..rowHeight). */
  offsetY: number;
  /** row height in px. */
  rowHeight: number;
  /** cursor X relative to the tree table's left edge (in px). */
  offsetX: number;
  /** target row tree depth (0 = root). */
  targetDepth: number;
  /** px-per-depth used for the "inside" threshold (default 24). */
  indentPx?: number;
}

/**
 * Asana-style drop-zone classifier. Zones use thirds so even short rows
 * give the user ~10px of vertical travel per decision band.
 *
 *   top 33%     → before
 *   bottom 33%  → after
 *   middle 33%  → inside  (when cursor is past `(depth+1) * indentPx`)
 *               → after   (otherwise)
 */
export function classifyDropPosition(input: DropClassifyInput): DropPosition {
  const { offsetY, rowHeight, offsetX, targetDepth } = input;
  const indent = input.indentPx ?? 24;
  const ratio = rowHeight > 0 ? offsetY / rowHeight : 0.5;
  if (ratio < 1 / 3) return 'before';
  if (ratio > 2 / 3) return 'after';
  const threshold = (targetDepth + 1) * indent;
  return offsetX >= threshold ? 'inside' : 'after';
}

export interface DescendantIndex {
  /** Given an itemId, the set of all descendant itemIds (not including itself). */
  get(itemId: string): ReadonlySet<string>;
}

export function buildDescendantIndex(
  items: readonly WbsItemDoc[],
): DescendantIndex {
  const byParent = new Map<string, WbsItemDoc[]>();
  for (const it of items) {
    const pid = it.parentItemId ?? '';
    const arr = byParent.get(pid);
    if (arr) arr.push(it);
    else byParent.set(pid, [it]);
  }
  const cache = new Map<string, Set<string>>();
  const collect = (id: string): Set<string> => {
    const cached = cache.get(id);
    if (cached) return cached;
    const out = new Set<string>();
    cache.set(id, out);
    const children = byParent.get(id) ?? [];
    for (const c of children) {
      out.add(c.itemId);
      for (const d of collect(c.itemId)) out.add(d);
    }
    return out;
  };
  return { get: (id) => collect(id) };
}

export interface MoveIntent {
  draggedId: string;
  targetId: string;
  position: DropPosition;
}

export interface ResolvedMove {
  draggedId: string;
  /** Resolved new parent after the drop. */
  newParentId: string | null;
  /** Ordered siblings as they will appear under the new parent after drop. */
  newSiblings: WbsItemDoc[];
  /** Existing siblings under the old parent, with the dragged row removed. */
  oldSiblings: WbsItemDoc[];
  /** True when the move actually changes parent. */
  parentChanged: boolean;
}

export type MoveError =
  | { kind: 'cycle' }
  | { kind: 'phase-must-be-root' }
  | { kind: 'missing-item' };

/**
 * Validate a drop. Returns the resolved move or an error describing why the
 * drop must be rejected. Only structural validation — callers handle toasts.
 */
export function resolveMove(
  items: readonly WbsItemDoc[],
  intent: MoveIntent,
  descendants: DescendantIndex,
): ResolvedMove | MoveError {
  const byId = new Map(items.map((i) => [i.itemId, i] as const));
  const dragged = byId.get(intent.draggedId);
  const target = byId.get(intent.targetId);
  if (!dragged || !target) return { kind: 'missing-item' };
  if (dragged.itemId === target.itemId) return { kind: 'cycle' };

  const newParentId =
    intent.position === 'inside' ? target.itemId : target.parentItemId;

  if (newParentId) {
    if (newParentId === dragged.itemId) return { kind: 'cycle' };
    if (descendants.get(dragged.itemId).has(newParentId)) {
      return { kind: 'cycle' };
    }
  }

  if (dragged.kind === 'phase' && newParentId !== null) {
    return { kind: 'phase-must-be-root' };
  }

  const oldParentKey = dragged.parentItemId ?? '';
  const newParentKey = newParentId ?? '';
  const parentChanged = oldParentKey !== newParentKey;

  const oldSiblings = sortSiblings(
    items.filter(
      (i) =>
        (i.parentItemId ?? '') === oldParentKey && i.itemId !== dragged.itemId,
    ),
  );

  const peers = sortSiblings(
    items.filter(
      (i) =>
        (i.parentItemId ?? '') === newParentKey && i.itemId !== dragged.itemId,
    ),
  );

  const newSiblings: WbsItemDoc[] = [];
  if (intent.position === 'inside') {
    newSiblings.push(...peers, dragged);
  } else {
    const anchorIdx = peers.findIndex((p) => p.itemId === intent.targetId);
    const insertIdx = anchorIdx === -1
      ? peers.length
      : intent.position === 'before'
      ? anchorIdx
      : anchorIdx + 1;
    newSiblings.push(
      ...peers.slice(0, insertIdx),
      dragged,
      ...peers.slice(insertIdx),
    );
  }

  return {
    draggedId: dragged.itemId,
    newParentId,
    newSiblings,
    oldSiblings,
    parentChanged,
  };
}

/**
 * Strip a leading 'P' used by phase-style keys so nested children can
 * inherit the numeric portion (e.g. "P1" → "1").
 */
function baseForChildren(parentSortKey: string): string {
  return parentSortKey.startsWith('P') ? parentSortKey.slice(1) : parentSortKey;
}

/**
 * Produce the sortKey that should be assigned to the nth sibling (0-indexed)
 * under `parent`. Root-level phases use the canonical "P0/P1/..." format;
 * nested rows extend the parent's numeric base with "-N" (1-indexed).
 */
export function computeSortKey(
  parent: WbsItemDoc | null,
  index0: number,
): string {
  if (!parent) return `P${index0}`;
  const base = baseForChildren(parent.sortKey);
  return `${base}-${index0 + 1}`;
}

export interface SortKeyAssignment {
  itemId: string;
  sortKey: string;
}

/**
 * Assign fresh sortKeys to every sibling in `ordered` based on their position.
 * Returns only the entries whose sortKey actually differs from the current
 * value (so callers can skip no-op writes).
 */
export function renumberSiblings(
  parent: WbsItemDoc | null,
  ordered: readonly WbsItemDoc[],
): SortKeyAssignment[] {
  const out: SortKeyAssignment[] = [];
  for (let i = 0; i < ordered.length; i++) {
    const next = computeSortKey(parent, i);
    if (ordered[i]!.sortKey !== next) {
      out.push({ itemId: ordered[i]!.itemId, sortKey: next });
    }
  }
  return out;
}

export interface MoveWriteSet {
  /** Writes: itemId → { parentItemId?, sortKey? }. */
  updates: Map<string, { parentItemId?: string | null; sortKey?: string }>;
}

/**
 * A valid `fractional-indexing` key is pure ASCII (upper/lower letters +
 * digits), contains no `-`, and doesn't match the legacy phase pattern
 * `P\d+` used by imported data. Anything outside these rules is treated
 * as a legacy key so we can migrate the whole sibling group before doing
 * any lexicographic "insert between neighbors" math.
 */
const FRACTIONAL_KEY_RE = /^[A-Za-z0-9]+$/;
const LEGACY_PHASE_RE = /^P\d+$/;

export function isFractionalSortKey(s: string): boolean {
  if (!s) return false;
  if (s.includes('-')) return false;
  if (LEGACY_PHASE_RE.test(s)) return false;
  return FRACTIONAL_KEY_RE.test(s);
}

function isGroupFractional(peers: readonly { sortKey: string }[]): boolean {
  for (const p of peers) {
    if (!isFractionalSortKey(p.sortKey)) return false;
  }
  return true;
}

/**
 * Given a sibling group (in display order), return the migration writes
 * required to convert it to fractional-indexing keys AND the effective
 * post-migration key of each sibling. If the group is already fractional
 * the migrations map is empty and effectiveKeys mirrors the input.
 */
export function ensureFractionalGroup(
  peers: readonly WbsItemDoc[],
): { migrations: Map<string, string>; effectiveKeys: string[] } {
  if (isGroupFractional(peers)) {
    return {
      migrations: new Map(),
      effectiveKeys: peers.map((p) => p.sortKey),
    };
  }
  if (peers.length === 0) {
    return { migrations: new Map(), effectiveKeys: [] };
  }
  const keys = generateNKeysBetween(null, null, peers.length);
  const migrations = new Map<string, string>();
  for (let i = 0; i < peers.length; i++) {
    migrations.set(peers[i]!.itemId, keys[i]!);
  }
  return { migrations, effectiveKeys: keys };
}

/**
 * Produce the batch of writes required to apply a resolved move.
 *
 * Strategy: fractional indexing.
 *   - The dragged row always gets 1 write (parentItemId when changed +
 *     a freshly-generated sortKey between its new left / right neighbors).
 *   - The OLD parent's remaining siblings are never touched — their
 *     relative order is preserved by their existing keys regardless of
 *     format.
 *   - The NEW parent's siblings are only touched on first-time migration:
 *     if any of them still carries a legacy path-style key ("P0", "1-5-3")
 *     we rewrite the whole group to fractional keys in a single batch so
 *     subsequent moves in that group collapse back to 1 write.
 */
export function buildMoveWrites(
  _items: readonly WbsItemDoc[],
  move: ResolvedMove,
): MoveWriteSet {
  const updates = new Map<
    string,
    { parentItemId?: string | null; sortKey?: string }
  >();

  const peers = move.newSiblings.filter((s) => s.itemId !== move.draggedId);
  const insertIdx = move.newSiblings.findIndex(
    (s) => s.itemId === move.draggedId,
  );

  const { migrations, effectiveKeys } = ensureFractionalGroup(peers);
  for (const [itemId, sortKey] of migrations) {
    updates.set(itemId, { sortKey });
  }

  const leftKey = insertIdx > 0 ? effectiveKeys[insertIdx - 1]! : null;
  const rightKey =
    insertIdx < effectiveKeys.length ? effectiveKeys[insertIdx]! : null;
  const newKey = generateKeyBetween(leftKey, rightKey);

  const draggedPatch: { parentItemId?: string | null; sortKey?: string } = {
    sortKey: newKey,
  };
  if (move.parentChanged) draggedPatch.parentItemId = move.newParentId;
  updates.set(move.draggedId, draggedPatch);

  return { updates };
}

/**
 * Allocate a sortKey for a brand-new row under `parentId`. Returns the
 * new key plus any sibling migrations required to convert the group to
 * fractional-indexing keys (empty map when the group is already
 * fractional). Callers should apply `migrations` in the same batch as
 * the new row's `setDoc` so the group never ends up with a mix of
 * formats on disk.
 */
export function computeAppendSortKey(
  items: readonly WbsItemDoc[],
  parentId: string | null,
): { sortKey: string; migrations: ReadonlyMap<string, string> } {
  const parentKey = parentId ?? '';
  const peers = sortSiblings(
    items.filter((i) => (i.parentItemId ?? '') === parentKey),
  );
  const { migrations, effectiveKeys } = ensureFractionalGroup(peers);
  const last = effectiveKeys.length > 0
    ? effectiveKeys[effectiveKeys.length - 1]!
    : null;
  return { sortKey: generateKeyBetween(last, null), migrations };
}

export function describeMoveError(err: MoveError): string {
  switch (err.kind) {
    case 'cycle':
      return '自分や子孫の中には移動できません';
    case 'phase-must-be-root':
      return 'phase 行は最上位にしか置けません';
    case 'missing-item':
      return '対象の行が見つかりませんでした';
  }
}
